import {useEffect, useState} from 'react';
import { useTheme } from '@mui/material/styles';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { alpha, Avatar, Box, Card, CardContent, CardHeader, colors, Divider, Grid2 as Grid, FormControl, InputAdornment, Link, OutlinedInput, Paper, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableRow, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useErrorBoundary } from 'react-error-boundary';
import { debounce} from 'lodash';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

interface FormCategory {
  categoryDescription: string;
  categoryId: string;
  items: any[]
}


const Forms = (): JSX.Element => {

  document.title = 'OCPF - Forms';

  //pass search from browser
  const queryParams = new URLSearchParams(window.location.search)

  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { showBoundary } = useErrorBoundary();  
  const [formCategories, setFormCategories] = useState<FormCategory[]>([]);
  const [allCategories, setAllCategories] = useState<FormCategory[]>([]);
  const [defaultSeachInput, setDefaultSearchInput] = useState<string>(queryParams.get('search') || "");

  useEffect(() => {
    getAllForms();
  }, [])

  const getAllForms = async () => {

    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/forms/all`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get all forms.'));
        }
        return result.json();
      })
      .then(result => {
        let forms = result;
        let categories: FormCategory[] = [];
        forms.forEach((form: any) => {
          let categoryId = form.categoryId;
          let categoryIndex = categories.findIndex((category) => category.categoryId === categoryId);
          if (categoryIndex > -1){
            categories[categoryIndex].items.push(form);
          }else{
            categories.push({
              categoryDescription: form.categoryDescription,
              categoryId: form.categoryId,
              items: [form]
            });
          }
        })
        setAllCategories(categories);
        if(queryParams.has('search')){
          let searchInput = queryParams.get('search');
          let filteredForms = categories.map((category) => ({
            ...category,
            items: category.items.filter((form) => form.title.toLowerCase().includes(searchInput) || form.description.toLowerCase().includes(searchInput))
          }));
          setFormCategories(filteredForms);
          return;
        }
        setFormCategories(categories);
      }
      );
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    if(searchInput.length === 0){
      setFormCategories(allCategories);
    }
    else{
      let filteredForms = allCategories.map((category) => ({
        ...category,
        items: category.items.filter((form) => form.title.toLowerCase().includes(searchInput) || form.description.toLowerCase().includes(searchInput))
      }));
      setFormCategories(filteredForms);
    }
  }, 800);

  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            gutterBottom
            marginBottom={2}
            >
            OCPF Forms
        </Typography>
        <Divider/>
        <Grid container marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1} sx={{ mb: 1}}>
                <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 300 }}>
                  <OutlinedInput
                      placeholder={"Search forms..."}
                      defaultValue={defaultSeachInput}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      onChange={filterByName}
                  />
                </FormControl>
            </Stack>
          </Grid>
          <Grid size={12} marginTop={3}>
          {
              formCategories && formCategories.length > 0 &&
              formCategories.map((category,i) =>
                   category.items.length > 0 &&
                   <Grid size={12} key={i} sx={{ marginBottom: 2 }}>
                       <Card variant="outlined">
                          <CardHeader
                              sx={{backgroundColor: "#fafafa"}}
                              title={<Typography variant='h6' color="primary" fontWeight={650}>{category.categoryDescription} Forms</Typography>}  
                          />
                          <Divider/>
                          <CardContent>
                            {
                              !isSM &&
                              <TableContainer component={Paper}>
                                  <Table aria-label="list of forms">
                                  <TableBody>
                                  {
                                  category.items?.map((form,i) =>
                                    <TableRow key={i}>
                                      <TableCell width={'5%'}>
                                        <Box
                                        component={Avatar}
                                        width={40}
                                        height={40}
                                        marginBottom={2}
                                        bgcolor={alpha(colors.teal[500], 0.1)}
                                        color={colors.teal[500]}
                                        variant={'rounded'}
                                        borderRadius={2}
                                        >
                                          <PictureAsPdfIcon />
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ textAlign: "left!important"}} width={'30%'}>
                                        <Typography variant="subtitle1" className="is-clickable">
                                            <Link marginRight={1} href={form.url} target="_blank" underline="none">{form.title}</Link>
                                        </Typography>
                                      </TableCell>
                                      <TableCell width={'65%'}>
                                         <Typography variant={'subtitle1'}>
                                            <span dangerouslySetInnerHTML={{ __html: form.description }} />
                                         </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )
                                  }
                                  </TableBody>
                                  </Table>
                              </TableContainer>
                            }
                            {
                              /*mobile view*/
                              isSM &&
                              category.items?.map((form,i) =>
                                <Card variant="outlined" key={i} sx={{ marginBottom: 2 }}>
                                  <CardContent>
                                    <Stack direction="row" spacing={1}>
                                      <PictureAsPdfIcon color={"primary"}/>
                                      <Typography variant="subtitle1" className="is-clickable">
                                        <Link marginRight={1} href={form.url} target="_blank" underline="none">{form.title}</Link>
                                      </Typography>
                                    </Stack>
                                    <Typography variant={'subtitle1'}>
                                      <span dangerouslySetInnerHTML={{ __html: form.description }} />
                                    </Typography>
                                  </CardContent>
                                </Card>
                              )
                            }
                          </CardContent>
                       </Card>
                   </Grid>
                )
          }         
          </Grid>
        </Grid>
      </Container>
    </Main>
  );
};

export default Forms;